import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { Button, Image, Panel } from 'react-bootstrap';
import SignInForm from '../../components/auth/SignInForm.jsx';
import { mapFinalFormErrors } from '../../lib/utils.js';
import { findUserOrganizations, signIn } from '../../modules/auth.js';
import { removeAuthHeaders } from '../../lib/session.js';
import SignInSsoForm from '../../components/auth/SignInSsoForm.jsx';
import { OktaIcon } from '../../components/icons/OktaIcon.jsx';

const mapErrors = mapFinalFormErrors('Failed to sign in');

let SignInFormContainer = ({
  signIn,
  organizationSlug,
  findUserOrganizations,
  isInModal,
  onSuccess,
}) => {
  const [isSso, setIsSso] = useState(false);
  const [userOrganizations, setUserOrganizations] = useState(null);

  const onFindOrganization = ({ email }) => {
    return findUserOrganizations(email)
      .then((res) => setUserOrganizations(res))
      .catch(mapErrors);
  };

  const onSsoSignIn = async (orgSsoConfig) => {
    if (typeof window !== 'undefined' && window.OktaAuth) {
      if (!orgSsoConfig.issuer || !orgSsoConfig.clientId) {
        throw new Error('SSO configuration error');
      }

      const oktaInstance = new window.OktaAuth({
        issuer: orgSsoConfig.issuer,
        clientId: orgSsoConfig.clientId,
        redirectUri: `${window.location.origin}/login/callback`,
        scopes: ['openid', 'profile', 'email'],
        pkce: true,
        responseType: 'code',
        state: btoa(JSON.stringify(orgSsoConfig)),
        tokenManager: {
          autoRenew: false,
        },
      });

      try {
        removeAuthHeaders();
        const params = new URLSearchParams(window.location.search);
        params.delete('error');
        const search = params.toString() ? `?${params.toString()}` : '';

        localStorage.setItem(
          'sso_return_url',
          window.location.pathname + search
        );
        await oktaInstance.signInWithRedirect({
          originalUri: window.location.href,
          state: btoa(JSON.stringify(orgSsoConfig)),
        });
      } catch (error) {
        throw new Error(
          'SSO authentication failed. Please contact your Okta administrator.'
        );
      }
    } else {
      throw new Error('SSO is not available');
    }
  };

  const onSubmit = (signIn, organizationSlug) => (values) => {
    if (onSuccess) return onSuccess(values);
    return signIn(values, organizationSlug).catch(mapErrors);
  };

  const validate = (values) => {
    const errors = {};

    if (isSso && !userOrganizations && !values.email) {
      errors.email = 'Email is required';
    }

    if (!isSso && !values.password) {
      errors.password = 'Password is required';
    }

    return errors;
  };

  return (
    <Panel>
      <Panel.Body>
        <Image
          alt="ChangeSync Logo"
          className="component-margin-bottom"
          src="/static/images/logo.svg"
          responsive
        />
        {isSso ? (
          <React.Fragment>
            <Form
              component={SignInSsoForm}
              onFindOrganization={onFindOrganization}
              userOrganizations={userOrganizations}
              onSubmit={(val) =>
                !userOrganizations
                  ? onFindOrganization(val)
                  : onSsoSignIn(val).catch(mapErrors)
              }
              validate={validate}
            />
            <button
              type="button"
              className="btn-clear bg-transparent btn-link flex m-t-24"
              onClick={() => {
                setIsSso(false);
                setUserOrganizations(null);
              }}
            >
              Other Ways to Sign In
            </button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Form
              component={SignInForm}
              onSubmit={onSubmit(signIn, organizationSlug)}
              validate={validate}
              isInModal={isInModal}
            />
            <div className="flex flex-justify flex-align-center m-b-18 m-t-16">
              <div
                style={{
                  width: '40%',
                  height: '1px',
                  backgroundColor: '#ccc',
                  marginRight: '10px',
                }}
              />
              <span className="text-muted" style={{ fontSize: '16px' }}>
                Or
              </span>
              <div
                style={{
                  width: '40%',
                  height: '1px',
                  backgroundColor: '#ccc',
                  marginLeft: '10px',
                }}
              />
            </div>

            <Button
              type="button"
              className="flex flex-center flex-align-center btn btn-outlined text-center w-100"
              onClick={() => setIsSso(!isSso)}
            >
              Sign In with &nbsp; <OktaIcon />
            </Button>
          </React.Fragment>
        )}
      </Panel.Body>
    </Panel>
  );
};

SignInFormContainer = connect(null, { signIn, findUserOrganizations })(
  SignInFormContainer
);

export default SignInFormContainer;
